import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProject, updateProject } from '../../services/projects';
import AppHeader from '../../components/AppHeader';
import ContextualActionBar from '../../components/ContextualActionBar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BasicInfoSection from './components/BasicInfoSection';
import SEOSection from './components/SEOSection';
import DomainSection from './components/DomainSection';
import IntegrationSection from './components/IntegrationSection';
import ContactFormSection from './components/ContactFormSection';
import SocialMediaSection from './components/SocialMediaSection';
import ThemeSection from './components/ThemeSection';

const SiteSettings = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const defaultFormData = {
    siteName: "",
    siteDescription: "",
    contactPhone: "",
    contactEmail: "",
    address: "",
    workingHoursWeekdays: "",
    workingHoursWeekends: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    domainType: "subdomain",
    subdomain: "",
    selectedDomain: "",
    customDomain: "",
    defaultLocationId: "",
    bookingApiKey: "",
    enableBookingWidget: false,
    googleAnalyticsId: "",
    enableAnalytics: false,
    yandexMetrikaId: "",
    enableYandexMetrika: false,
    enableContactForm: true,
    contactFormType: "email",
    notificationEmail: "",
    telegramBotToken: "",
    telegramChatId: "",
    webhookUrl: "",
    formFields: {
      name: true,
      phone: true,
      email: true,
      message: true
    },
    vkUrl: "",
    telegramUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    youtubeUrl: "",
    whatsappUrl: "",
    theme: "default"
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});

  // Load project settings from Firestore
  useEffect(() => {
    async function loadProject() {
      if (!projectId) {
        setLoading(false);
        return;
      }
      try {
        const project = await getProject(projectId);
        setCurrentProject(project);
        if (project) {
          // Merge settings with top-level domain fields (for backwards compatibility)
          // Top-level fields take precedence as they are denormalized for querying
          setFormData(prev => ({
            ...prev,
            ...project.settings,
            siteName: project.name,
            // Ensure domain fields are loaded from top-level or settings
            domainType: project.domainType || project.settings?.domainType || prev.domainType,
            subdomain: project.subdomain || project.settings?.subdomain || prev.subdomain,
            selectedDomain: project.selectedDomain || project.settings?.selectedDomain || prev.selectedDomain,
            customDomain: project.customDomain || project.settings?.customDomain || prev.customDomain,
          }));
        }
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    }
    loadProject();
  }, [projectId]);

  const tabs = [
  { id: 'basic', label: 'Основное', icon: 'Info' },
  { id: 'theme', label: 'Тема', icon: 'Palette' },
  { id: 'seo', label: 'SEO', icon: 'Search' },
  { id: 'domain', label: 'Домен', icon: 'Globe' },
  { id: 'integrations', label: 'Интеграции', icon: 'Plug' },
  { id: 'contact', label: 'Форма связи', icon: 'Mail' },
  { id: 'social', label: 'Соцсети', icon: 'Share2' }];


  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setHasUnsavedChanges(true);

    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.siteName?.trim()) {
      newErrors.siteName = 'Название сайта обязательно';
    }

    if (!formData?.contactPhone?.trim()) {
      newErrors.contactPhone = 'Телефон обязателен';
    }

    if (!formData?.contactEmail?.trim()) {
      newErrors.contactEmail = 'Email обязателен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      newErrors.contactEmail = 'Некорректный email';
    }

    if (formData?.domainType === 'subdomain' && !formData?.subdomain?.trim()) {
      newErrors.subdomain = 'Поддомен обязателен';
    }

    if (formData?.domainType === 'custom' && !formData?.customDomain?.trim()) {
      newErrors.customDomain = 'Домен обязателен';
    }

    if (formData?.enableContactForm) {
      if (formData?.contactFormType === 'email' && !formData?.notificationEmail?.trim()) {
        newErrors.notificationEmail = 'Email для уведомлений обязателен';
      }
      if (formData?.contactFormType === 'telegram') {
        if (!formData?.telegramBotToken?.trim()) {
          newErrors.telegramBotToken = 'Токен бота обязателен';
        }
        if (!formData?.telegramChatId?.trim()) {
          newErrors.telegramChatId = 'Chat ID обязателен';
        }
      }
      if (formData?.contactFormType === 'webhook' && !formData?.webhookUrl?.trim()) {
        newErrors.webhookUrl = 'Webhook URL обязателен';
      }
    }

    setErrors(newErrors);

    // Return both validity and errors for handleSave to use
    return { isValid: Object.keys(newErrors)?.length === 0, errors: newErrors };
  };

  const handleSave = async () => {
    const { isValid, errors: validationErrors } = validateForm();

    if (!isValid) {
      // Switch to tab with first error to show user what's wrong
      const errorFields = Object.keys(validationErrors);
      if (errorFields.length > 0) {
        const basicFields = ['siteName', 'siteDescription', 'contactPhone', 'contactEmail', 'address'];
        const domainFields = ['subdomain', 'customDomain', 'domainType'];
        const contactFields = ['notificationEmail', 'telegramBotToken', 'telegramChatId', 'webhookUrl'];

        const firstError = errorFields[0];
        if (basicFields.includes(firstError)) {
          setActiveTab('basic');
        } else if (domainFields.includes(firstError)) {
          setActiveTab('domain');
        } else if (contactFields.includes(firstError)) {
          setActiveTab('contact');
        }
      }
      return;
    }

    setIsSaving(true);

    try {
      if (projectId) {
        // Save to Firestore
        // subdomain, selectedDomain and customDomain are denormalized to top-level for Firestore querying
        await updateProject(projectId, {
          name: formData.siteName,
          domainType: formData.domainType,
          subdomain: formData.domainType === 'subdomain' ? formData.subdomain?.toLowerCase() || null : null,
          selectedDomain: formData.domainType === 'subdomain' ? formData.selectedDomain || null : null,
          customDomain: formData.domainType === 'custom' ? formData.customDomain?.toLowerCase() || null : null,
          settings: formData
        });
      }
      setHasUnsavedChanges(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Ошибка сохранения настроек');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    if (projectId) {
      navigate(`/projects/${projectId}/preview`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `site-settings-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
        currentProject={currentProject}
        onLogout={handleLogout}
      />

      <main className="pt-14 lg:pt-[104px] pb-20 lg:pb-8">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Настройки сайта
                </h1>
                <p className="text-sm md:text-base text-text-secondary">
                  Управление параметрами и интеграциями вашего сайта
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/dashboard')}
                className="self-start sm:self-auto">

                К сайтам
              </Button>
            </div>

            {showSuccessMessage &&
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
                <p className="text-sm text-success font-medium">
                  Настройки успешно сохранены
                </p>
              </div>
            }
          </div>

          <div className="bg-card rounded-xl shadow-elevation-2 overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex min-w-max lg:min-w-0">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab?.id ?
                  'text-primary border-b-2 border-primary bg-primary/5' : 'text-text-secondary hover:text-foreground hover:bg-muted'}`
                  }>

                    <Icon name={tab?.icon} size={18} />
                    <span className="hidden sm:inline">{tab?.label}</span>
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {activeTab === 'basic' &&
              <BasicInfoSection
                formData={formData}
                errors={errors}
                onChange={handleChange} />

              }

              {activeTab === 'theme' &&
              <ThemeSection
                formData={formData}
                onChange={handleChange} />

              }

              {activeTab === 'seo' &&
              <SEOSection
                formData={formData}
                errors={errors}
                onChange={handleChange} />

              }

              {activeTab === 'domain' &&
              <DomainSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onCheckDomain={() => {}}
                projectId={projectId} />

              }

              {activeTab === 'integrations' &&
              <IntegrationSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onTestConnection={() => {}} />

              }

              {activeTab === 'contact' &&
              <ContactFormSection
                formData={formData}
                errors={errors}
                onChange={handleChange} />

              }

              {activeTab === 'social' &&
              <SocialMediaSection
                formData={formData}
                errors={errors}
                onChange={handleChange} />

              }
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="outline"
              iconName="Eye"
              iconPosition="left"
              onClick={handlePreview}>

              Предпросмотр
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleExport}>

              Экспорт настроек
            </Button>
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasUnsavedChanges}>

              {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
            </Button>
          </div>
        </div>
      </main>
      <ContextualActionBar
        onSave={handleSave}
        onPreview={handlePreview}
        onExport={handleExport}
        onPublish={() => {}}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges} />

    </div>);

};

export default SiteSettings;